export enum CropConnectLocale {
  DaDK = 'da-DK',
  DeDE = 'de-DE',
  FiFI = 'fi-FI',
  LtLT = 'lt-LT',
  PlPL = 'pl-PL',
  SkSK = 'sk-SK',
  SvSE = 'sv-SE',
  EnGB = 'en-GB',
}

// Mapping for ISO country codes to CropConnectLocale
const countryToLocaleMap: Record<string, CropConnectLocale> = {
  DK: CropConnectLocale.DaDK,
  DE: CropConnectLocale.DeDE,
  FI: CropConnectLocale.FiFI,
  LT: CropConnectLocale.LtLT,
  PL: CropConnectLocale.PlPL,
  SK: CropConnectLocale.SkSK,
  SE: CropConnectLocale.SvSE,
  GB: CropConnectLocale.EnGB,
};

/**
 * Maps Latvian users to Lithuanian locale for specific use cases.
 * To be used only in components where LV->LT mapping is required.
 *
 * @param locale - The locale string or CropConnectLocale to check
 * @returns CropConnectLocale - Lithuanian locale for LV inputs, original locale otherwise
 */
export const mapLocaleAccordingToNzRules = (
  locale: string | CropConnectLocale | undefined
): CropConnectLocale => {
  if (!locale) return CropConnectLocale.DeDE;

  const normalizedLocale = locale.toString().toUpperCase();

  // Handle LV -> LT special case
  if (normalizedLocale.includes('LV')) {
    return CropConnectLocale.LtLT;
  }

  // Handle two-letter country codes (PL, DE, etc.)
  if (normalizedLocale.length === 2) {
    return countryToLocaleMap[normalizedLocale] || CropConnectLocale.DeDE;
  }

  // Handle full locale formats (PlPL, DeDE, etc.)
  if (Object.values(CropConnectLocale).includes(locale as CropConnectLocale)) {
    return locale as CropConnectLocale;
  }

  return CropConnectLocale.DeDE;
};

// ISO 639-1 language codes
export const getLanguageCodeFromLocale = (
  locale: CropConnectLocale
): string => {
  switch (locale) {
    case CropConnectLocale.DaDK:
      return 'da';
    case CropConnectLocale.DeDE:
      return 'de';
    case CropConnectLocale.FiFI:
      return 'fi';
    case CropConnectLocale.LtLT:
      return 'lt';
    case CropConnectLocale.PlPL:
      return 'pl';
    case CropConnectLocale.SkSK:
      return 'sk';
    case CropConnectLocale.SvSE:
      return 'sv';
    case CropConnectLocale.EnGB:
      return 'en';
    default:
      return 'de'; // Fallback to German
  }
};

export const getLanguageCodeForCountry = (countryIsoCode?: string): string => {
  const locale = convertCountryToLocale(countryIsoCode);
  return getLanguageCodeFromLocale(locale);
};

const convertCountryToLocale = (countryIsoCode?: string): CropConnectLocale => {
  if (!countryIsoCode) {
    countryIsoCode = process.env.REACT_APP_DEFAULT_LOCALE_FORMAT_COUNTRY
      ? process.env.REACT_APP_DEFAULT_LOCALE_FORMAT_COUNTRY
      : (() => {
          throw new Error(
            '"env.REACT_APP_DEFAULT_LOCALE_FORMAT_COUNTRY" was not provided'
          );
        })();
  }

  return mapLocaleAccordingToNzRules(countryIsoCode);
};

export const formatNumber = (
  value: number | string,
  countryIsoCode: string | undefined
): string => {
  const numberValue = typeof value === 'number' ? value : parseFloat(value);
  let options: Intl.NumberFormatOptions = {};
  const countryLocale = convertCountryToLocale(countryIsoCode);

  switch (countryLocale) {
    case CropConnectLocale.DaDK:
    case CropConnectLocale.DeDE:
    case CropConnectLocale.PlPL:
    case CropConnectLocale.LtLT:
    case CropConnectLocale.SkSK:
      options = {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 10,
        useGrouping: true,
      };
      break;
    case CropConnectLocale.SvSE:
    case CropConnectLocale.FiFI:
      options = {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 10,
        useGrouping: true,
      };
      break;
    default:
      options = {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 10,
        useGrouping: true,
      };
      break;
  }

  return new Intl.NumberFormat(countryLocale, options).format(numberValue);
};

export const formatNumberWithDecimals = (
  value: number | string,
  countryIsoCode: string | undefined,
  decimals: number
): string => {
  const numberValue = typeof value === 'number' ? value : parseFloat(value);
  const countryLocale = convertCountryToLocale(countryIsoCode);

  const options: Intl.NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  };

  return new Intl.NumberFormat(countryLocale, options).format(numberValue);
};

export const formatDate = (
  date: Date | string,
  countryIsoCode: string | undefined
): string => {
  const countryLocale = convertCountryToLocale(countryIsoCode);

  let dateValue: Date;
  if (typeof date === 'string') {
    const newsDateFormat = /^\d{4}-\d{2}-\d{2}\d{2}:\d{2}:\d{2}$/;
    if (newsDateFormat.test(date)) {
      const correctedDateString = date.replace(
        /(\d{4}-\d{2}-\d{2})(\d{2}:\d{2}:\d{2})/,
        '$1T$2'
      );
      dateValue = new Date(correctedDateString);
    } else {
      dateValue = new Date(date);
    }
  } else {
    dateValue = date;
  }

  const options = getDateTimeFormatOptions(countryLocale);
  return new Intl.DateTimeFormat(countryLocale, options).format(dateValue);
};

const getDateTimeFormatOptions = (
  countryLocale: CropConnectLocale
): Intl.DateTimeFormatOptions => {
  switch (countryLocale) {
    case CropConnectLocale.DaDK:
    case CropConnectLocale.DeDE:
    case CropConnectLocale.PlPL:
    case CropConnectLocale.LtLT:
    case CropConnectLocale.SkSK:
    case CropConnectLocale.FiFI:
      return { day: '2-digit', month: '2-digit', year: 'numeric' };
    case CropConnectLocale.SvSE:
      return { year: 'numeric', month: '2-digit', day: '2-digit' };
    default:
      return { year: 'numeric', month: 'long', day: 'numeric' };
  }
};

//mapper for FDTM module language props, eg. en-GB to en
export const convertToExternalLanguageCode = (
  internalLocale: CropConnectLocale | string
): string => {
  const SUPPORTED_LANGUAGES = ['en', 'de', 'pl', 'da', 'lt', 'sk', 'sv', 'fi'];

  // Handle both formats: 'en-GB' and 'EnGB'
  const localeString = internalLocale.toString();
  let rawCode: string;

  if (localeString.includes('-')) {
    // Handle hyphenated format like 'en-GB'
    rawCode = localeString.split('-')[0].toLowerCase();
  } else {
    // Handle CropConnectLanguages format like 'EnGB'
    rawCode = localeString.substring(0, 2).toLowerCase();
  }

  const isValid = SUPPORTED_LANGUAGES.includes(rawCode);

  return isValid ? rawCode : 'en';
};
